import { useState } from "react";

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Serif+Display:ital@0;1&family=DM+Sans:wght@300;400;500;600;700&display=swap');

  :root {
    --green-dark: #2D5016;
    --green-mid:  #3A6B1A;
    --green-leaf: #4E8B2A;
    --green-light:#7AB648;
    --cream:      #F5F3EE;
    --cream-dark: #EAE7DF;
    --charcoal:   #1C1C1C;
    --muted:      #6B7280;
    --white:      #FFFFFF;
    --accent-gold:#C8A84B;
  }

  * { margin:0; padding:0; box-sizing:border-box; }

  body {
    font-family: 'DM Sans', sans-serif;
    background: var(--white);
    color: var(--charcoal);
    font-size: 11pt;
    line-height: 1.65;
  }

  .page {
    width: 210mm;
    min-height: 297mm;
    margin: 0 auto;
    position: relative;
    overflow: hidden;
    background: var(--white);
  }
  .page-break { page-break-after: always; }

  /* COVER */
  .cover { background: var(--green-dark); min-height: 297mm; display: flex; flex-direction: column; }
  .cover-split { display: flex; flex: 1; }
  .cover-left { flex: 1.1; background: var(--green-dark); padding: 56px 48px; display: flex; flex-direction: column; justify-content: space-between; position: relative; }
  .cover-right { flex: 0.9; background: var(--cream); padding: 56px 44px; display: flex; flex-direction: column; justify-content: center; position: relative; }
  .cover-right::before { content:''; position:absolute; top:0; left:-30px; width: 60px; height: 100%; background: var(--cream); clip-path: polygon(30px 0, 100% 0, 100% 100%, 0 100%); }
  .cover-logo { display:flex; align-items:center; gap:12px; }
  .cover-logo-shield { width:44px; height:44px; background: rgba(255,255,255,0.15); border-radius:10px; display:flex; align-items:center; justify-content:center; }
  .cover-logo-name { font-family: 'DM Serif Display', serif; font-size: 22pt; color: var(--white); letter-spacing: 3px; }
  .cover-tag { font-size: 8pt; letter-spacing: 3px; text-transform: uppercase; color: var(--green-light); font-weight: 600; margin-bottom: 16px; }
  .cover-headline { font-family: 'DM Serif Display', serif; font-size: 38pt; color: var(--white); line-height: 1.15; margin-bottom: 24px; }
  .cover-headline em { color: var(--green-light); font-style: italic; }
  .cover-sub { font-size: 11pt; color: rgba(255,255,255,0.7); line-height: 1.7; max-width: 340px; }
  .cover-badges { display: flex; gap: 20px; margin-top: 40px; }
  .cover-badge { font-size: 7.5pt; letter-spacing: 2.5px; text-transform: uppercase; color: rgba(255,255,255,0.5); font-weight:600; }
  .cover-badge::before { content: '·'; margin-right:8px; color: var(--green-light); }
  .cover-right-label { font-size: 8pt; letter-spacing: 3px; text-transform: uppercase; color: var(--green-mid); font-weight: 700; margin-bottom: 10px; }
  .cover-right h2 { font-family: 'DM Serif Display', serif; font-size: 28pt; color: var(--charcoal); line-height: 1.2; margin-bottom: 28px; }
  .cover-right-stat { display: flex; flex-direction: column; gap: 18px; margin-top: 10px; }
  .stat-row { display: flex; align-items: flex-start; gap: 14px; padding: 14px 16px; background: var(--white); border-radius: 10px; border-left: 4px solid var(--green-leaf); }
  .stat-row-num { font-family: 'DM Serif Display', serif; font-size: 22pt; color: var(--green-dark); line-height:1; }
  .stat-row-label { font-size: 9pt; color: var(--muted); margin-top: 4px; }
  .cover-diagonal { position: absolute; bottom: 0; right: 0; width: 120px; height: 120px; background: var(--green-leaf); clip-path: polygon(100% 0, 100% 100%, 0 100%); opacity: 0.3; }

  /* TOC */
  .toc-page { padding: 60px 60px; min-height: 297mm; display: flex; flex-direction: column; }
  .section-label { font-size: 8pt; letter-spacing: 3px; text-transform: uppercase; color: var(--green-mid); font-weight: 700; margin-bottom: 8px; }
  .section-title { font-family: 'DM Serif Display', serif; font-size: 34pt; color: var(--charcoal); line-height: 1.1; margin-bottom: 48px; }
  .toc-list { list-style: none; }
  .toc-item { display: flex; align-items: center; padding: 18px 0; border-bottom: 1px solid var(--cream-dark); gap: 20px; }
  .toc-num { font-family: 'DM Serif Display', serif; font-size: 28pt; color: var(--cream-dark); line-height: 1; min-width: 50px; }
  .toc-text { flex: 1; }
  .toc-text strong { display: block; font-size: 13pt; font-weight: 600; color: var(--charcoal); margin-bottom: 3px; }
  .toc-text span { font-size: 9.5pt; color: var(--muted); }
  .toc-page-num { font-size: 9pt; color: var(--green-mid); font-weight: 700; letter-spacing: 1px; }
  .toc-accent-bar { width: 6px; height: 40px; background: var(--green-leaf); border-radius: 3px; flex-shrink: 0; }

  /* INNER PAGES */
  .inner-page { min-height: 297mm; display: flex; flex-direction: column; }
  .inner-header { background: var(--green-dark); padding: 28px 60px; display: flex; align-items: center; justify-content: space-between; }
  .inner-header-brand { display: flex; align-items: center; gap: 10px; }
  .inner-header-brand-shield { width: 32px; height: 32px; background: rgba(255,255,255,0.15); border-radius: 8px; display:flex; align-items:center; justify-content:center; }
  .inner-header-brand-name { font-family: 'DM Serif Display', serif; font-size: 14pt; color: var(--white); letter-spacing: 2px; }
  .inner-header-brand-sub { font-size: 7pt; letter-spacing: 2px; text-transform: uppercase; color: rgba(255,255,255,0.5); margin-top: 1px; }
  .inner-header-section { font-size: 8pt; letter-spacing: 2.5px; text-transform: uppercase; color: rgba(255,255,255,0.55); font-weight:600; }
  .inner-body { flex: 1; padding: 44px 60px; }
  .inner-footer { background: var(--cream); padding: 14px 60px; display: flex; justify-content: space-between; align-items: center; }
  .inner-footer-text { font-size: 8pt; color: var(--muted); letter-spacing: 1.5px; text-transform: uppercase; }
  .inner-footer-pg { font-size: 8pt; color: var(--green-mid); font-weight: 700; }

  /* INTRO */
  .intro-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 32px; margin-top: 32px; }
  .intro-text p { font-size: 11pt; color: var(--charcoal); line-height: 1.75; margin-bottom: 16px; }
  .quote-block { background: var(--green-dark); border-radius: 14px; padding: 32px; color: var(--white); position: relative; overflow: hidden; }
  .quote-block::after { content: '"'; font-family: 'DM Serif Display', serif; font-size: 120pt; color: rgba(255,255,255,0.06); position: absolute; top: -20px; right: 10px; line-height: 1; }
  .quote-block p { font-family: 'DM Serif Display', serif; font-size: 15pt; font-style: italic; line-height: 1.5; margin-bottom: 16px; position: relative; z-index: 1; }
  .quote-block cite { font-size: 9pt; letter-spacing: 2px; text-transform: uppercase; color: var(--green-light); font-weight: 600; font-style: normal; }
  .mission-box { grid-column: 1 / -1; background: var(--cream); border-radius: 14px; padding: 28px 32px; display: flex; gap: 24px; align-items: flex-start; }
  .mission-icon { width: 48px; height: 48px; background: var(--green-dark); border-radius: 12px; flex-shrink: 0; display: flex; align-items: center; justify-content: center; }
  .mission-box h3 { font-family: 'DM Serif Display', serif; font-size: 15pt; margin-bottom: 8px; color: var(--charcoal); }
  .mission-box p { font-size: 10.5pt; color: var(--muted); line-height: 1.7; }
  .sectors-strip { display: flex; gap: 12px; margin-top: 28px; flex-wrap: wrap; }
  .sector-chip { background: var(--cream); border: 1px solid var(--cream-dark); border-radius: 30px; padding: 8px 18px; font-size: 9pt; color: var(--green-dark); font-weight: 600; letter-spacing: 0.5px; }

  /* PRODUCT */
  .product-intro { font-size: 11pt; color: var(--muted); line-height: 1.75; max-width: 640px; margin-bottom: 36px; }
  .module-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 14px; margin-bottom: 36px; }
  .module-card { background: var(--cream); border-radius: 12px; padding: 20px 16px; text-align: center; border-top: 3px solid var(--green-leaf); }
  .module-card-icon { width: 40px; height: 40px; background: var(--green-dark); border-radius: 10px; margin: 0 auto 12px; display:flex; align-items:center; justify-content:center; }
  .module-card h4 { font-size: 9.5pt; font-weight: 700; color: var(--charcoal); }
  .features-list { list-style: none; display: grid; grid-template-columns: 1fr 1fr; gap: 10px; margin-top: 8px; }
  .features-list li { display: flex; align-items: flex-start; gap: 10px; font-size: 10pt; color: var(--charcoal); }
  .features-list li::before { content: '✓'; background: var(--green-dark); color: var(--white); font-size: 8pt; width: 18px; height: 18px; border-radius: 50%; display: flex; align-items: center; justify-content: center; flex-shrink: 0; margin-top: 2px; }

  /* SCREEN MOCK */
  .screen-mock { background: var(--charcoal); border-radius: 12px; padding: 10px; margin: 20px 0; }
  .screen-mock-bar { display: flex; gap: 5px; margin-bottom: 8px; }
  .screen-mock-bar span { width: 8px; height: 8px; border-radius: 50%; }
  .screen-mock-inner { background: var(--cream); border-radius: 8px; padding: 20px; display: grid; grid-template-columns: 160px 1fr; gap: 0; min-height: 130px; }
  .mock-sidebar { background: var(--green-dark); border-radius: 6px 0 0 6px; padding: 16px 12px; }
  .mock-sidebar-item { font-size: 7.5pt; color: rgba(255,255,255,0.6); padding: 5px 8px; border-radius: 5px; margin-bottom: 3px; }
  .mock-sidebar-item.active { background: rgba(255,255,255,0.15); color: var(--white); font-weight: 600; }
  .mock-main { background: var(--white); border-radius: 0 6px 6px 0; padding: 16px; }
  .mock-stats { display: grid; grid-template-columns: repeat(4, 1fr); gap: 8px; margin-bottom: 12px; }
  .mock-stat { background: var(--cream); border-radius: 6px; padding: 8px 10px; }
  .mock-stat-label { font-size: 6pt; color: var(--muted); text-transform: uppercase; letter-spacing: 1px; }
  .mock-stat-value { font-size: 11pt; font-weight: 700; color: var(--charcoal); margin-top: 2px; }
  .mock-table-row { display: flex; gap: 8px; padding: 6px 0; border-bottom: 1px solid var(--cream-dark); font-size: 7pt; color: var(--muted); align-items: center; }
  .mock-table-row span { flex:1; }
  .mock-badge { font-size: 6pt; padding: 2px 7px; border-radius: 20px; font-weight: 700; letter-spacing: 0.5px; }
  .mock-badge.done { background: #DCFCE7; color: #15803D; }
  .mock-badge.active-badge { background: #FEF9C3; color: #854D0E; }

  /* TRIAL BALANCE */
  .trial-balance-mock { background: var(--white); border: 1px solid var(--cream-dark); border-radius: 12px; overflow: hidden; margin: 20px 0; }
  .tb-header { background: var(--green-dark); padding: 14px 20px; display: flex; justify-content: space-between; align-items: center; }
  .tb-header span { font-size: 9pt; font-weight: 700; color: var(--white); letter-spacing: 1px; }
  .tb-header small { font-size: 8pt; color: rgba(255,255,255,0.5); }
  .type-badge { font-size: 7pt; padding: 2px 8px; border-radius: 20px; font-weight: 700; background: #DCFCE7; color: #15803D; }
  .type-badge.income { background: #EDE9FE; color: #5B21B6; }

  /* VALUE PROP */
  .value-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; margin-top: 32px; }
  .value-card { border-radius: 14px; padding: 28px; position: relative; overflow: hidden; }
  .value-card.dark { background: var(--green-dark); color: var(--white); }
  .value-card.light { background: var(--cream); border: 1px solid var(--cream-dark); }
  .value-num { font-family: 'DM Serif Display', serif; font-size: 60pt; line-height:1; color: rgba(255,255,255,0.08); position: absolute; bottom: -10px; right: 10px; }
  .value-num.lnum { color: rgba(0,0,0,0.04); }
  .value-card h3 { font-family: 'DM Serif Display', serif; font-size: 17pt; margin-bottom: 10px; }
  .value-card.dark h3 { color: var(--white); }
  .value-card.light h3 { color: var(--charcoal); }
  .value-card p { font-size: 10pt; line-height: 1.7; }
  .value-card.dark p { color: rgba(255,255,255,0.7); }
  .value-card.light p { color: var(--muted); }
  .value-pill { display: inline-flex; align-items: center; gap: 6px; margin-bottom: 14px; background: rgba(255,255,255,0.12); border-radius: 30px; padding: 4px 12px; font-size: 8pt; font-weight: 700; letter-spacing: 1.5px; text-transform: uppercase; color: var(--green-light); }
  .value-pill.lpill { background: rgba(46,93,22,0.1); color: var(--green-mid); }

  /* BENEFITS */
  .benefits-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; margin-top: 28px; }
  .benefit-card { background: var(--cream); border-radius: 14px; padding: 26px; position: relative; border-left: 5px solid var(--green-leaf); }
  .benefit-num { font-family: 'DM Serif Display', serif; font-size: 40pt; color: var(--cream-dark); line-height: 1; margin-bottom: 12px; }
  .benefit-card h3 { font-size: 12pt; font-weight: 700; color: var(--green-dark); margin-bottom: 8px; line-height: 1.3; }
  .benefit-card p { font-size: 9.5pt; color: var(--muted); line-height: 1.7; }

  /* REPORTS */
  .reports-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 14px; margin-top: 28px; }
  .report-card { border: 1px solid var(--cream-dark); border-radius: 12px; padding: 18px; background: var(--white); }
  .report-card-tag { font-size: 7pt; letter-spacing: 2px; text-transform: uppercase; color: var(--muted); font-weight: 600; margin-bottom: 6px; }
  .report-card h4 { font-size: 11pt; font-weight: 700; color: var(--charcoal); margin-bottom: 14px; }
  .report-btns { display: flex; gap: 8px; }
  .report-btn-pdf { background: var(--green-dark); color: var(--white); border: none; border-radius: 7px; padding: 6px 14px; font-size: 8pt; font-weight: 700; cursor: pointer; letter-spacing: 0.5px; }
  .report-btn-csv { background: transparent; color: var(--green-dark); border: 1px solid var(--cream-dark); border-radius: 7px; padding: 6px 14px; font-size: 8pt; font-weight: 700; cursor: pointer; }

  /* PRICING */
  .pricing-table { width: 100%; border-collapse: collapse; margin-top: 28px; border-radius: 14px; overflow: hidden; border: 1px solid var(--cream-dark); }
  .pricing-table thead tr { background: var(--green-dark); }
  .pricing-table thead th { padding: 18px 16px; text-align: left; font-size: 9pt; font-weight: 700; letter-spacing: 1.5px; text-transform: uppercase; color: var(--white); }
  .pricing-table thead th:first-child { color: rgba(255,255,255,0.7); }
  .pricing-table thead th.highlight { background: var(--green-leaf); }
  .pricing-table tbody tr { border-bottom: 1px solid var(--cream-dark); }
  .pricing-table tbody tr:nth-child(even) { background: var(--cream); }
  .pricing-table tbody td { padding: 13px 16px; font-size: 9.5pt; color: var(--charcoal); }
  .pricing-table tbody td:first-child { font-weight: 600; color: var(--green-dark); }
  .pricing-table tfoot td { padding: 10px 16px; font-size: 9pt; color: var(--muted); background: var(--cream); font-style: italic; }
  .check { color: var(--green-leaf); font-size: 14pt; font-weight: 700; }
  .price-head { font-family: 'DM Serif Display', serif; font-size: 20pt; color: var(--white); display: block; line-height: 1; }
  .price-sub { font-size: 8pt; color: rgba(255,255,255,0.5); font-weight:400; display:block; margin-top:4px; }

  /* CTA */
  .cta-page { min-height: 297mm; background: var(--green-dark); display: flex; flex-direction: column; position: relative; overflow: hidden; }
  .cta-page::before { content: ''; position: absolute; bottom: -80px; right: -80px; width: 360px; height: 360px; border-radius: 50%; background: rgba(255,255,255,0.03); }
  .cta-page::after { content: ''; position: absolute; top: -60px; left: -60px; width: 250px; height: 250px; border-radius: 50%; background: rgba(255,255,255,0.03); }
  .cta-header { background: rgba(0,0,0,0.2); padding: 28px 60px; display: flex; align-items: center; justify-content: space-between; position: relative; z-index:2; }
  .cta-brand { display:flex; align-items:center; gap:10px; }
  .cta-brand-shield { width:32px; height:32px; background: rgba(255,255,255,0.15); border-radius:8px; display:flex; align-items:center; justify-content:center; }
  .cta-brand-name { font-family: 'DM Serif Display', serif; font-size: 14pt; color: var(--white); letter-spacing: 2px; }
  .cta-brand-sub { font-size:7pt; letter-spacing:2px; text-transform:uppercase; color:rgba(255,255,255,0.4); }
  .cta-section-label { font-size:8pt; letter-spacing:3px; color:rgba(255,255,255,0.4); text-transform:uppercase; font-weight:700; }
  .cta-body { flex:1; padding: 60px; display: flex; flex-direction: column; justify-content: center; position: relative; z-index:2; }
  .cta-tag { font-size: 8pt; letter-spacing: 3px; text-transform: uppercase; color: var(--green-light); font-weight: 700; margin-bottom: 16px; }
  .cta-headline { font-family: 'DM Serif Display', serif; font-size: 42pt; color: var(--white); line-height: 1.15; margin-bottom: 24px; }
  .cta-headline em { font-style: italic; color: var(--green-light); }
  .cta-desc { font-size: 12pt; color: rgba(255,255,255,0.65); max-width: 500px; line-height: 1.75; margin-bottom: 48px; }
  .contact-cards { display: grid; grid-template-columns: repeat(3, 1fr); gap: 16px; }
  .contact-card { background: rgba(255,255,255,0.08); border-radius: 14px; padding: 22px; border: 1px solid rgba(255,255,255,0.1); }
  .contact-card-icon { width: 38px; height: 38px; background: rgba(255,255,255,0.12); border-radius: 10px; display: flex; align-items: center; justify-content: center; margin-bottom: 14px; }
  .contact-card-label { font-size: 8pt; letter-spacing: 2px; text-transform: uppercase; color: rgba(255,255,255,0.4); font-weight: 700; margin-bottom: 6px; }
  .contact-card-value { font-size: 10.5pt; color: var(--white); font-weight: 600; line-height: 1.5; }
  .cta-footer { padding: 22px 60px; background: rgba(0,0,0,0.2); display: flex; justify-content: space-between; align-items: center; position: relative; z-index:2; }
  .cta-footer-text { font-size: 8pt; color: rgba(255,255,255,0.3); letter-spacing: 1.5px; text-transform: uppercase; }
  .cta-footer-pg { font-size: 8pt; color: var(--green-light); font-weight: 700; }
  .steps-row { display: grid; grid-template-columns: repeat(3, 1fr); gap: 16px; margin-bottom: 40px; }
  .step-card { background: rgba(255,255,255,0.07); border-radius: 12px; padding: 20px; border: 1px solid rgba(255,255,255,0.08); }
  .step-num { font-family: 'DM Serif Display', serif; font-size: 28pt; color: rgba(255,255,255,0.15); line-height:1; margin-bottom:10px; }
  .step-card h4 { font-size:11pt; font-weight:700; color:var(--white); margin-bottom:6px; }
  .step-card p { font-size:9pt; color:rgba(255,255,255,0.55); line-height:1.65; }

  /* NAV */
  .nav-bar { position: fixed; top: 0; left: 0; right: 0; background: var(--green-dark); z-index: 100; padding: 12px 40px; display: flex; align-items: center; justify-content: space-between; box-shadow: 0 2px 16px rgba(0,0,0,0.3); }
  .nav-brand { font-family: 'DM Serif Display', serif; font-size: 16pt; color: var(--white); letter-spacing: 3px; }
  .nav-pages { display: flex; gap: 6px; }
  .nav-btn { background: rgba(255,255,255,0.1); border: none; color: rgba(255,255,255,0.7); padding: 5px 12px; border-radius: 20px; font-size: 8pt; cursor: pointer; font-family: 'DM Sans', sans-serif; font-weight: 600; letter-spacing: 1px; transition: all 0.2s; }
  .nav-btn:hover { background: rgba(255,255,255,0.2); color: var(--white); }
  .nav-btn.active { background: var(--green-leaf); color: var(--white); }
  .page-wrapper { padding-top: 56px; }
`;

const ShieldIcon = ({ size = 24 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 2L4 6v6c0 5.5 3.8 10.7 8 12 4.2-1.3 8-6.5 8-12V6l-8-4z" />
  </svg>
);

const pages = ["Cover", "TOC", "Intro", "Modules", "Loans", "Accounting", "Value", "Benefits", "Pricing", "Contact"];

// ── PAGE 1: COVER ──────────────────────────────────────────
function CoverPage() {
  return (
    <div className="page page-break">
      <div className="cover">
        <div className="cover-split">
          <div className="cover-left">
            <div className="cover-logo">
              <div className="cover-logo-shield"><ShieldIcon size={24} /></div>
              <div className="cover-logo-name">KREPP</div>
            </div>
            <div>
              <div className="cover-tag">Financial Platform · Product Proposal</div>
              <div className="cover-headline">
                A Grounded<br />Platform for<br />People Who<br /><em>Lend with Care.</em>
              </div>
              <div className="cover-sub">
                KREPP is a purpose-built financial platform for microfinance institutions, cooperative lenders, and community banks — combining savings management, loan origination, repayment tracking, double-entry accounting, and branded reporting in one secure environment.
              </div>
            </div>
            <div>
              <div className="cover-badges">
                <span className="cover-badge">Secure</span>
                <span className="cover-badge">Intelligent</span>
                <span className="cover-badge">Smart</span>
              </div>
            </div>
            <div className="cover-diagonal"></div>
          </div>
          <div className="cover-right">
            <div>
              <div className="cover-right-label">Platform Overview</div>
              <h2>Manage Savings, Loans &amp; Reporting — Seamlessly.</h2>
              <div className="cover-right-stat">
                {[
                  { num: "360°", label: "Financial Operations Coverage" },
                  { num: "10+", label: "Branded Report Types (PDF & CSV)" },
                  { num: "3", label: "Subscription Tiers — Starter to Enterprise" },
                  { num: "100%", label: "Double-Entry Accounting with Trial Balance" },
                ].map((s, i) => (
                  <div key={i} className="stat-row">
                    <div>
                      <div className="stat-row-num">{s.num}</div>
                      <div className="stat-row-label">{s.label}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── PAGE 2: TOC ────────────────────────────────────────────
function TocPage() {
  const items = [
    { num: "01", title: "Introduction & Overview", sub: "Company background, mission, and target markets", pg: "03" },
    { num: "02", title: "Platform Modules", sub: "Dashboard, Customers, Savings, Loans & Finance", pg: "04" },
    { num: "03", title: "Accounting & Reporting", sub: "Double-entry ledger, trial balance, 10+ report types", pg: "06" },
    { num: "04", title: "Value Proposition", sub: "Security, intelligence, simplicity, and reliability", pg: "07" },
    { num: "05", title: "Key Benefits", sub: "How KREPP improves lending operations end-to-end", pg: "08" },
    { num: "06", title: "Pricing & Plans", sub: "Starter, Growth, and Enterprise subscription tiers", pg: "09" },
    { num: "07", title: "Next Steps & Contact", sub: "Demo request, onboarding, and support channels", pg: "10" },
  ];
  return (
    <div className="page page-break">
      <div className="toc-page">
        <div className="section-label">Document Structure</div>
        <div className="section-title">Table of<br />Contents</div>
        <ul className="toc-list">
          {items.map((item) => (
            <li key={item.num} className="toc-item">
              <div className="toc-accent-bar"></div>
              <div className="toc-num">{item.num}</div>
              <div className="toc-text">
                <strong>{item.title}</strong>
                <span>{item.sub}</span>
              </div>
              <div className="toc-page-num">{item.pg}</div>
            </li>
          ))}
        </ul>
        <div style={{ marginTop: "auto", paddingTop: 40, display: "flex", gap: 10, alignItems: "center" }}>
          <div style={{ width: 36, height: 36, background: "var(--green-dark)", borderRadius: 9, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <ShieldIcon size={20} />
          </div>
          <div>
            <div style={{ fontFamily: "'DM Serif Display',serif", fontSize: "13pt", color: "var(--charcoal)", letterSpacing: 2 }}>KREPP</div>
            <div style={{ fontSize: "7pt", letterSpacing: 2, textTransform: "uppercase", color: "var(--muted)" }}>Financial Platform</div>
          </div>
          <div style={{ marginLeft: "auto", fontSize: "8pt", color: "var(--muted)" }}>KREPP PROPOSAL · PAGE 02</div>
        </div>
      </div>
    </div>
  );
}

// ── PAGE 3: INTRODUCTION ───────────────────────────────────
function IntroPage() {
  return (
    <div className="page page-break">
      <div className="inner-page">
        <InnerHeader section="Introduction · 01" />
        <div className="inner-body">
          <div className="section-label">About KREPP</div>
          <div className="section-title" style={{ fontSize: "28pt", marginBottom: 28 }}>Introduction</div>
          <div className="intro-grid">
            <div className="intro-text">
              <p>Managing a lending institution involves far more than issuing loans. From capturing customer savings and tracking repayment schedules to maintaining a balanced general ledger and generating compliant financial reports — the operational demands are significant.</p>
              <p>KREPP was built to address these challenges head-on. Our platform consolidates every aspect of lending operations — customer management, savings accounts, loan origination, repayment schedules, income tracking, double-entry accounting, and branded reporting — into a single, secure, cloud-accessible system.</p>
              <p>Whether you run a community cooperative with 50 members or a growing microfinance institution with thousands of borrowers, KREPP scales to your needs without sacrificing simplicity.</p>
              <div className="sectors-strip">
                {["Microfinance Institutions", "Cooperatives", "Community Banks", "Credit Unions", "Savings Groups"].map(s => (
                  <span key={s} className="sector-chip">{s}</span>
                ))}
              </div>
            </div>
            <div className="quote-block">
              <p>A grounded platform for people who lend with care — secure, intelligent, and built for the realities of modern lending.</p>
              <cite>KREPP · Platform Manifesto</cite>
            </div>
            <div className="mission-box">
              <div className="mission-icon">
                <svg width="26" height="26" viewBox="0 0 24 24" fill="white"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 14.5v-9l6 4.5-6 4.5z" /></svg>
              </div>
              <div>
                <h3>Our Mission</h3>
                <p>To empower lending institutions with fully functional, reliable, and affordable technology — enabling them to manage savings, disburse loans, track repayments, and produce accurate financial reports with zero compromise on data security or operational transparency.</p>
              </div>
            </div>
          </div>
        </div>
        <InnerFooter page="03" />
      </div>
    </div>
  );
}

// ── PAGE 4: PLATFORM MODULES ───────────────────────────────
function ModulesPage() {
  const modules = [
    { label: "Dashboard", icon: <svg viewBox="0 0 24 24" fill="white" width="22" height="22"><rect x="3" y="3" width="8" height="8" rx="1" /><rect x="13" y="3" width="8" height="8" rx="1" /><rect x="3" y="13" width="8" height="8" rx="1" /><rect x="13" y="13" width="8" height="8" rx="1" /></svg> },
    { label: "Customers", icon: <svg viewBox="0 0 24 24" fill="white" width="22" height="22"><path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z" /></svg> },
    { label: "Savings", icon: <svg viewBox="0 0 24 24" fill="white" width="22" height="22"><path d="M21 18v1c0 1.1-.9 2-2 2H5c-1.11 0-2-.9-2-2V5c0-1.1.89-2 2-2h14c1.1 0 2 .9 2 2v1h-9c-1.11 0-2 .9-2 2v8c0 1.1.89 2 2 2h9zm-9-2h10V8H12v8zm4-2.5c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5z" /></svg> },
    { label: "Loans", icon: <svg viewBox="0 0 24 24" fill="white" width="22" height="22"><path d="M11.8 10.9c-2.27-.59-3-1.2-3-2.15 0-1.09 1.01-1.85 2.7-1.85 1.78 0 2.44.85 2.5 2.1h2.21c-.07-1.72-1.12-3.3-3.21-3.81V3h-3v2.16c-1.94.42-3.5 1.68-3.5 3.61 0 2.31 1.91 3.46 4.7 4.13 2.5.6 3 1.48 3 2.41 0 .69-.49 1.79-2.7 1.79-2.06 0-2.87-.92-2.98-2.1h-2.2c.12 2.19 1.76 3.42 3.68 3.83V21h3v-2.15c1.95-.37 3.5-1.5 3.5-3.55 0-2.84-2.43-3.81-4.7-4.4z" /></svg> },
    { label: "Finance", icon: <svg viewBox="0 0 24 24" fill="white" width="22" height="22"><path d="M16 6l2.29 2.29-4.88 4.88-4-4L2 16.59 3.41 18l6-6 4 4 6.3-6.29L22 12V6z" /></svg> },
    { label: "Staff", icon: <svg viewBox="0 0 24 24" fill="white" width="22" height="22"><path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-7 3c1.93 0 3.5 1.57 3.5 3.5S13.93 13 12 13s-3.5-1.57-3.5-3.5S10.07 6 12 6zm7 13H5v-.23c0-.62.28-1.2.76-1.58C7.47 15.82 9.64 15 12 15s4.53.82 6.24 2.19c.48.38.76.97.76 1.58V19z" /></svg> },
    { label: "Reports", icon: <svg viewBox="0 0 24 24" fill="white" width="22" height="22"><path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-5 14H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z" /></svg> },
    { label: "Billing", icon: <svg viewBox="0 0 24 24" fill="white" width="22" height="22"><path d="M20 4H4c-1.11 0-2 .89-2 2v12c0 1.11.89 2 2 2h16c1.11 0 2-.89 2-2V6c0-1.11-.89-2-2-2zm0 14H4v-6h16v6zm0-10H4V6h16v2z" /></svg> },
  ];
  return (
    <div className="page page-break">
      <div className="inner-page">
        <InnerHeader section="Platform Modules · 02" />
        <div className="inner-body">
          <div className="section-label">Core Modules</div>
          <div className="section-title" style={{ fontSize: "26pt", marginBottom: 16 }}>Platform Overview</div>
          <div className="product-intro">KREPP is structured around eight tightly integrated modules, each purpose-built for a specific function within the lending lifecycle. Together they provide an unbroken operational chain from customer onboarding to financial reporting.</div>
          <div className="module-grid">
            {modules.map(m => (
              <div key={m.label} className="module-card">
                <div className="module-card-icon">{m.icon}</div>
                <h4>{m.label}</h4>
              </div>
            ))}
          </div>
          <div className="screen-mock">
            <div className="screen-mock-bar">
              <span style={{ background: "#FF5F57" }}></span>
              <span style={{ background: "#FEBC2E" }}></span>
              <span style={{ background: "#28C840" }}></span>
            </div>
            <div className="screen-mock-inner">
              <div className="mock-sidebar">
                <div style={{ fontSize: "8pt", fontWeight: 700, color: "white", letterSpacing: 1, marginBottom: 14 }}>KREPP</div>
                {["Dashboard", "Customers", "Savings", "Loans", "Finance", "Accounting"].map((item, i) => (
                  <div key={item} className={`mock-sidebar-item${i === 0 ? " active" : ""}`}>{item}</div>
                ))}
              </div>
              <div className="mock-main">
                <div style={{ fontSize: "7pt", color: "var(--muted)", letterSpacing: 1, textTransform: "uppercase", marginBottom: 4 }}>Operations Dashboard</div>
                <div style={{ fontSize: "13pt", fontWeight: 700, marginBottom: 12 }}>Company Overview</div>
                <div className="mock-stats">
                  {[{ l: "Customers", v: "142" }, { l: "Total Savings", v: "₦4.2M" }, { l: "Active Loans", v: "38" }, { l: "Outstanding", v: "₦1.9M" }].map(s => (
                    <div key={s.l} className="mock-stat">
                      <div className="mock-stat-label">{s.l}</div>
                      <div className="mock-stat-value">{s.v}</div>
                    </div>
                  ))}
                </div>
                <div style={{ fontSize: "8pt", fontWeight: 700, marginBottom: 6 }}>Recent Loans</div>
                <div className="mock-table-row">
                  <span>22/05/2026</span><span>Francis A.</span><span>FLAT</span><span>₦1,000</span>
                  <span className="mock-badge done">COMPLETED</span>
                </div>
                <div className="mock-table-row">
                  <span>13/05/2026</span><span>Grace O.</span><span>REDUCING</span><span>₦500,000</span>
                  <span className="mock-badge active-badge">ACTIVE</span>
                </div>
              </div>
            </div>
          </div>
        </div>
        <InnerFooter page="04" />
      </div>
    </div>
  );
}

// ── PAGE 5: LOANS ──────────────────────────────────────────
function LoansPage() {
  const loanFeatures = ["Flat & Reducing Balance Methods", "Automated Repayment Schedules", "Per-Installment Status Tracking", "Loan Approval Workflow", "Waiver & Write-Off Support", "Outstanding Balance Visibility", "Multi-Loan per Customer", "Interest & Principal Split"];
  const savingsFeatures = ["Individual Savings Accounts", "Unique Account ID Generation", "Deposit & Withdrawal Tracking", "Real-Time Balance Display", "Account Status Management", "Customer Name Search", "Transaction History Log", "Multiple Accounts per Customer"];
  const scheduleRows = [
    { date: "21 Jun 2026", sched: "₦133.33", prin: "₦83.33", int: "₦50.00", close: "₦0.00", paid: "₦133.33", status: "PAID", statusStyle: { background: "#DCFCE7", color: "#15803D" } },
    { date: "21 Jul 2026", sched: "₦133.33", prin: "₦83.33", int: "₦50.00", close: "₦0.00", paid: "₦966.67", status: "PAID", statusStyle: { background: "#DCFCE7", color: "#15803D" } },
    { date: "20 Aug 2026", sched: "₦0.00", prin: "₦0.00", int: "₦0.00", close: "₦0.00", paid: "₦0.00", status: "WAIVED", statusStyle: { background: "#F3F4F6", color: "var(--muted)" } },
    { date: "19 Sep 2026", sched: "₦0.00", prin: "₦0.00", int: "₦0.00", close: "₦0.00", paid: "₦0.00", status: "WAIVED", statusStyle: { background: "#F3F4F6", color: "var(--muted)" } },
  ];
  return (
    <div className="page page-break">
      <div className="inner-page">
        <InnerHeader section="Loans & Savings · 02" />
        <div className="inner-body">
          <div className="section-label">Origination · Approval · Repayment</div>
          <div className="section-title" style={{ fontSize: "26pt", marginBottom: 12 }}>Loans &amp; Savings</div>
          <div className="product-intro">KREPP handles the full loan lifecycle — from origination and approval through to structured repayment schedules, with flat or reducing-balance interest methods and automated status tracking per installment.</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24, marginBottom: 28 }}>
            <div>
              <div style={{ fontSize: "11pt", fontWeight: 700, color: "var(--green-dark)", marginBottom: 14, paddingBottom: 8, borderBottom: "2px solid var(--cream-dark)" }}>Loan Module Features</div>
              <ul className="features-list">{loanFeatures.map(f => <li key={f}>{f}</li>)}</ul>
            </div>
            <div>
              <div style={{ fontSize: "11pt", fontWeight: 700, color: "var(--green-dark)", marginBottom: 14, paddingBottom: 8, borderBottom: "2px solid var(--cream-dark)" }}>Savings Module Features</div>
              <ul className="features-list">{savingsFeatures.map(f => <li key={f}>{f}</li>)}</ul>
            </div>
          </div>
          <div style={{ background: "var(--cream)", borderRadius: 12, padding: 20 }}>
            <div style={{ fontSize: "10pt", fontWeight: 700, color: "var(--charcoal)", marginBottom: 14 }}>Sample Repayment Schedule — Loan · f4274ebc</div>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "9pt" }}>
              <thead>
                <tr style={{ background: "var(--cream-dark)" }}>
                  {["Due Date", "Scheduled", "Principal", "Interest", "Closing Bal.", "Amount Paid", "Status"].map((h, i) => (
                    <th key={h} style={{ padding: "9px 12px", textAlign: i === 0 ? "left" : i === 6 ? "center" : "right", fontSize: "7.5pt", fontWeight: 700, textTransform: "uppercase", letterSpacing: 1, color: "var(--muted)" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {scheduleRows.map((r, i) => (
                  <tr key={i} style={{ borderBottom: "1px solid var(--cream-dark)", color: r.status === "WAIVED" ? "var(--muted)" : "inherit" }}>
                    <td style={{ padding: "9px 12px" }}>{r.date}</td>
                    <td style={{ padding: "9px 12px", textAlign: "right" }}>{r.sched}</td>
                    <td style={{ padding: "9px 12px", textAlign: "right" }}>{r.prin}</td>
                    <td style={{ padding: "9px 12px", textAlign: "right" }}>{r.int}</td>
                    <td style={{ padding: "9px 12px", textAlign: "right" }}>{r.close}</td>
                    <td style={{ padding: "9px 12px", textAlign: "right" }}>{r.paid}</td>
                    <td style={{ padding: "9px 12px", textAlign: "center" }}>
                      <span style={{ ...r.statusStyle, fontSize: "7.5pt", fontWeight: 700, padding: "2px 10px", borderRadius: 20 }}>{r.status}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div style={{ marginTop: 14, display: "flex", gap: 28, fontSize: "9pt", padding: "10px 12px", background: "var(--white)", borderRadius: 8, border: "1px solid var(--cream-dark)" }}>
              <span><strong style={{ color: "var(--green-dark)" }}>Principal:</strong> ₦1,000.00</span>
              <span><strong style={{ color: "var(--green-dark)" }}>Rate:</strong> 5% monthly</span>
              <span><strong style={{ color: "var(--green-dark)" }}>Total Payable:</strong> ₦1,600.00</span>
              <span><strong style={{ color: "var(--green-dark)" }}>Outstanding:</strong> ₦0.00</span>
            </div>
          </div>
        </div>
        <InnerFooter page="05" />
      </div>
    </div>
  );
}

// ── PAGE 6: ACCOUNTING ─────────────────────────────────────
function AccountingPage() {
  const tbRows = [
    { code: "1000", name: "Cash", type: "ASSET", typeClass: "", debit: "₦1,734,433.32", credit: "₦1,001,000.00", bal: "₦733,433.32", balColor: "var(--green-dark)" },
    { code: "1100", name: "Loan Receivable", type: "ASSET", typeClass: "", debit: "₦1,001,000.00", credit: "₦1,284,333.32", bal: "-₦283,333.32", balColor: "#DC2626" },
    { code: "4000", name: "Interest Income", type: "INCOME", typeClass: "income", debit: "₦0.00", credit: "₦450,100.00", bal: "-₦450,100.00", balColor: "#DC2626" },
  ];
  const reports = [
    { tag: "Company", title: "Loan Portfolio" }, { tag: "Company", title: "Loans Summary" },
    { tag: "Company", title: "Savings Book" }, { tag: "Company", title: "Upcoming Repayments" },
    { tag: "Company", title: "Income Statement (P&L)" }, { tag: "Company", title: "Balance Sheet" },
  ];
  return (
    <div className="page page-break">
      <div className="inner-page">
        <InnerHeader section="Accounting & Reports · 03" />
        <div className="inner-body">
          <div className="section-label">Chart of Accounts · Journal · Trial Balance</div>
          <div className="section-title" style={{ fontSize: "26pt", marginBottom: 12 }}>Accounting &amp; Reporting</div>
          <div className="product-intro">KREPP includes a full double-entry accounting engine. Every financial event — loan disbursement, repayment, income, expense — automatically posts to the journal, keeping your books balanced at all times.</div>
          <div className="trial-balance-mock">
            <div className="tb-header">
              <span>Trial Balance</span>
              <small>All Time · Auto-generated</small>
            </div>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "9pt" }}>
              <thead>
                <tr style={{ background: "var(--cream)" }}>
                  {["Code", "Account", "Type", "Debit", "Credit", "Balance"].map((h, i) => (
                    <th key={h} style={{ padding: "10px 14px", textAlign: i <= 1 ? "left" : i === 2 ? "center" : "right", fontSize: "7.5pt", fontWeight: 700, letterSpacing: 1, textTransform: "uppercase", color: "var(--muted)" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {tbRows.map(r => (
                  <tr key={r.code} style={{ borderBottom: "1px solid var(--cream-dark)" }}>
                    <td style={{ padding: "10px 14px" }}>{r.code}</td>
                    <td style={{ padding: "10px 14px" }}>{r.name}</td>
                    <td style={{ padding: "10px 14px", textAlign: "center" }}><span className={`type-badge ${r.typeClass}`}>{r.type}</span></td>
                    <td style={{ padding: "10px 14px", textAlign: "right" }}>{r.debit}</td>
                    <td style={{ padding: "10px 14px", textAlign: "right" }}>{r.credit}</td>
                    <td style={{ padding: "10px 14px", textAlign: "right", fontWeight: 700, color: r.balColor }}>{r.bal}</td>
                  </tr>
                ))}
                <tr style={{ background: "var(--cream)", fontWeight: 700 }}>
                  <td style={{ padding: "10px 14px" }}></td>
                  <td style={{ padding: "10px 14px", color: "var(--charcoal)" }}>TOTAL</td>
                  <td style={{ padding: "10px 14px" }}></td>
                  <td style={{ padding: "10px 14px", textAlign: "right" }}>₦2,735,433.32</td>
                  <td style={{ padding: "10px 14px", textAlign: "right" }}>₦2,735,433.32</td>
                  <td style={{ padding: "10px 14px", textAlign: "right", color: "var(--green-dark)" }}>₦0.00</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div style={{ display: "flex", gap: 8, margin: "20px 0 10px", flexWrap: "wrap" }}>
            {["Trial Balance", "Journal", "Chart of Accounts", "General Ledger"].map((tab, i) => (
              <span key={tab} style={{ background: i === 0 ? "var(--green-dark)" : "var(--cream)", color: i === 0 ? "var(--white)" : "var(--muted)", padding: "6px 16px", borderRadius: 30, fontSize: "8.5pt", fontWeight: i === 0 ? 700 : 600 }}>{tab}</span>
            ))}
          </div>
          <div style={{ fontSize: "11pt", fontWeight: 700, color: "var(--charcoal)", margin: "20px 0 12px" }}>Available Reports (10)</div>
          <div className="reports-grid">
            {reports.map(r => (
              <div key={r.title} className="report-card">
                <div className="report-card-tag">{r.tag}</div>
                <h4>{r.title}</h4>
                <div className="report-btns">
                  <button className="report-btn-pdf">↓ PDF</button>
                  <button className="report-btn-csv">CSV</button>
                </div>
              </div>
            ))}
          </div>
        </div>
        <InnerFooter page="06" />
      </div>
    </div>
  );
}

// ── PAGE 7: VALUE PROPOSITION ──────────────────────────────
function ValuePage() {
  const cards = [
    { variant: "dark", pill: "01", title: "Security First", body: "End-to-end data encryption, role-based access controls, and full audit trails ensure that sensitive borrower and financial data is always protected — meeting institutional standards for data governance.", numClass: "" },
    { variant: "light", pill: "02", title: "Intelligent Automation", body: "Repayment schedules, interest calculations, journal postings, and trial balances are all generated automatically — eliminating manual spreadsheet work and the errors that come with it.", numClass: "lnum" },
    { variant: "light", pill: "03", title: "Radical Simplicity", body: "A clean, minimal interface means staff at every level — from loan officers to administrators — can navigate confidently without lengthy training or technical expertise.", numClass: "lnum" },
    { variant: "dark", pill: "04", title: "Proven Reliability", body: "Built on modern infrastructure with real-time data sync, KREPP is designed for the availability your institution needs. Whether serving 50 or 5,000 customers, the platform stays consistent and fast.", numClass: "" },
  ];
  return (
    <div className="page page-break">
      <div className="inner-page">
        <InnerHeader section="Value Proposition · 04" />
        <div className="inner-body">
          <div className="section-label">Why KREPP</div>
          <div className="section-title" style={{ fontSize: "26pt", marginBottom: 12 }}>Our Value Proposition</div>
          <div className="product-intro">The promise is simple: a secure, intelligent, and affordable platform that removes operational friction from lending — leaving institutions free to focus on the relationships and decisions that matter.</div>
          <div className="value-grid">
            {cards.map(c => (
              <div key={c.pill} className={`value-card ${c.variant}`}>
                <div className={`value-pill${c.variant === "light" ? " lpill" : ""}`}>{c.pill}</div>
                <h3>{c.title}</h3>
                <p>{c.body}</p>
                <div className={`value-num ${c.numClass}`}>{c.pill}</div>
              </div>
            ))}
          </div>
          <div style={{ background: "var(--cream)", borderRadius: 14, padding: "24px 28px", marginTop: 24, display: "flex", alignItems: "center", gap: 20 }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontFamily: "'DM Serif Display',serif", fontSize: "14pt", color: "var(--charcoal)", marginBottom: 6 }}>The Promise is Simple…</div>
              <div style={{ fontSize: "10pt", color: "var(--muted)", lineHeight: 1.7 }}>We offer a secure, intelligent, and affordable financial operations platform — providing complete visibility into lending activity, savings portfolios, and financial health, all from a single dashboard.</div>
            </div>
            <div style={{ display: "flex", gap: 16 }}>
              {[{ num: "100%", label: "Data Encrypted" }, { num: "Zero", label: "Manual Journals" }].map(s => (
                <div key={s.label} style={{ textAlign: "center" }}>
                  <div style={{ fontFamily: "'DM Serif Display',serif", fontSize: "24pt", color: "var(--green-dark)", lineHeight: 1 }}>{s.num}</div>
                  <div style={{ fontSize: "8pt", color: "var(--muted)", textTransform: "uppercase", letterSpacing: 1 }}>{s.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
        <InnerFooter page="07" />
      </div>
    </div>
  );
}

// ── PAGE 8: KEY BENEFITS ───────────────────────────────────
function BenefitsPage() {
  const benefits = [
    { num: "01", title: "Eliminate Manual Bookkeeping Entirely", body: "Every loan disbursement, repayment, savings deposit, and income entry automatically posts to the double-entry journal. Your trial balance stays up to date without a single manual entry." },
    { num: "02", title: "Track Every Borrower's Status in Real Time", body: "The loans module provides installment-level visibility — each repayment is marked as Paid, Pending, or Waived. Outstanding balances update automatically, giving loan officers an accurate picture at every moment." },
    { num: "03", title: "Generate Audit-Ready Reports in Seconds", body: "With 10 pre-built report types including the Trial Balance, Income Statement, Balance Sheet, Loan Portfolio, and Savings Book — available as branded PDFs or CSV exports — KREPP eliminates report-preparation delays." },
    { num: "04", title: "Manage Staff Access with Granular Controls", body: "The Staff module lets administrators assign roles and permissions precisely. Loan officers, accountants, and executives each see exactly what they need — and nothing they shouldn't." },
    { num: "05", title: "Scale from 100 to Unlimited Customers", body: "KREPP's subscription model grows with your institution. Start on the Starter plan with up to 100 customers and 5 staff users. Upgrade to Growth or Enterprise as your portfolio expands." },
    { num: "06", title: "Access from Anywhere, on Any Device", body: "KREPP is a web-based platform accessible from any modern browser — desktop, tablet, or mobile. No software installation, no local database maintenance." },
  ];
  return (
    <div className="page page-break">
      <div className="inner-page">
        <InnerHeader section="Key Benefits · 05" />
        <div className="inner-body">
          <div className="section-label">Benefits to Your Institution</div>
          <div className="section-title" style={{ fontSize: "26pt", marginBottom: 12 }}>Key Benefits</div>
          <div className="product-intro">KREPP transforms how lending institutions operate day-to-day — reducing manual work, eliminating data silos, and giving decision-makers the financial clarity they need.</div>
          <div className="benefits-grid">
            {benefits.map(b => (
              <div key={b.num} className="benefit-card">
                <div className="benefit-num">{b.num}</div>
                <h3>{b.title}</h3>
                <p>{b.body}</p>
              </div>
            ))}
          </div>
        </div>
        <InnerFooter page="08" />
      </div>
    </div>
  );
}

// ── PAGE 9: PRICING ────────────────────────────────────────
function PricingPage() {
  const rows = [
    ["Customers", "Up to 100", "Up to 1,000", "Unlimited"],
    ["Staff Users", "5 users", "25 users", "Unlimited"],
    ["Loans Management", "✓", "✓", "✓"],
    ["Savings Accounts", "✓", "✓", "✓"],
    ["Finance (Income & Expense)", "✓", "✓", "✓"],
    ["Double-Entry Accounting", "✓", "✓", "✓"],
    ["Standard Reports (10 types)", "✓", "✓", "✓"],
    ["Email Support", "✓", "–", "–"],
    ["Priority Support", "–", "✓", "✓"],
    ["Advanced Reports & Branding", "–", "✓", "✓"],
    ["Custom Integrations", "–", "–", "✓"],
    ["White-Label Option", "–", "–", "✓"],
    ["Dedicated Account Manager", "–", "–", "✓"],
  ];
  return (
    <div className="page page-break">
      <div className="inner-page">
        <InnerHeader section="Pricing & Plans · 06" />
        <div className="inner-body">
          <div className="section-label">Subscription · Stripe Checkout</div>
          <div className="section-title" style={{ fontSize: "26pt", marginBottom: 12 }}>Pricing &amp; Plans</div>
          <div className="product-intro">KREPP offers three clear subscription tiers — billed monthly or annually. All plans include the full feature set; tiers differ only in the number of customers and staff seats your institution requires.</div>
          <table className="pricing-table">
            <thead>
              <tr>
                <th>Feature</th>
                <th>STARTER<br /><span className="price-head">₦50,000</span><span className="price-sub">per month</span></th>
                <th className="highlight">GROWTH<br /><span className="price-head">₦100,000</span><span className="price-sub">per month · Most Popular</span></th>
                <th>ENTERPRISE<br /><span className="price-head">₦150,000</span><span className="price-sub">per month</span></th>
              </tr>
            </thead>
            <tbody>
              {rows.map(([feat, s, g, e], i) => (
                <tr key={i}>
                  <td>{feat}</td>
                  {[s, g, e].map((v, j) => (
                    <td key={j} className={v === "✓" ? "check" : ""}>{v}</td>
                  ))}
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr><td colSpan={4}>All plans include a free trial period. Annual billing available at a discount. Stripe-powered secure checkout.</td></tr>
            </tfoot>
          </table>
        </div>
        <InnerFooter page="09" />
      </div>
    </div>
  );
}

// ── PAGE 10: CONTACT ───────────────────────────────────────
function ContactPage() {
  return (
    <div className="page">
      <div className="cta-page">
        <div className="cta-header">
          <div className="cta-brand">
            <div className="cta-brand-shield"><ShieldIcon size={18} /></div>
            <div>
              <div className="cta-brand-name">KREPP</div>
              <div className="cta-brand-sub">Financial Platform</div>
            </div>
          </div>
          <div className="cta-section-label">Next Steps · 07</div>
        </div>
        <div className="cta-body">
          <div className="cta-tag">Ready to Get Started?</div>
          <div className="cta-headline">Let's Build<br />a Smarter<br /><em>Lending Operation.</em></div>
          <div className="cta-desc">The next step is simple — request a live demo and see KREPP working with your own data. Our onboarding team will walk you through every module, answer your questions, and help you select the right plan for your institution.</div>
          <div className="steps-row">
            {[
              { n: "01", title: "Request a Demo", body: "Schedule a live walkthrough with our team. We'll tailor the session to your institution's specific workflow and lending model." },
              { n: "02", title: "Choose Your Plan", body: "Select the subscription tier that matches your customer count and staff size. Start your free trial — no credit card required." },
              { n: "03", title: "Go Live in Days", body: "Our onboarding team handles setup, data migration, and staff training. Most institutions are fully operational within one week." },
            ].map(s => (
              <div key={s.n} className="step-card">
                <div className="step-num">{s.n}</div>
                <h4>{s.title}</h4>
                <p>{s.body}</p>
              </div>
            ))}
          </div>
          <div className="contact-cards">
            {[
              { label: "Email", value: "Kreppmail@gmail.com", icon: <svg viewBox="0 0 24 24" fill="white" width="20" height="20"><path d="M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z" /></svg> },
              { label: "Website", value: "www.kreppng.com", icon: <svg viewBox="0 0 24 24" fill="white" width="20" height="20"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 14.5v-9l6 4.5-6 4.5z" /></svg> },
              { label: "Request Demo", value: "www.kreppng.com/demo", icon: <svg viewBox="0 0 24 24" fill="white" width="20" height="20"><path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z" /></svg> },
            ].map(c => (
              <div key={c.label} className="contact-card">
                <div className="contact-card-icon">{c.icon}</div>
                <div className="contact-card-label">{c.label}</div>
                <div className="contact-card-value">{c.value}</div>
              </div>
            ))}
          </div>
        </div>
        <div className="cta-footer">
          <div className="cta-footer-text">KREPP Financial Platform · Proposal · Confidential · 2026</div>
          <div className="cta-footer-pg">Page 10</div>
        </div>
      </div>
    </div>
  );
}

// ── SHARED COMPONENTS ──────────────────────────────────────
function InnerHeader({ section }) {
  return (
    <div className="inner-header">
      <div className="inner-header-brand">
        <div className="inner-header-brand-shield"><ShieldIcon size={18} /></div>
        <div>
          <div className="inner-header-brand-name">KREPP</div>
          <div className="inner-header-brand-sub">Financial Platform</div>
        </div>
      </div>
      <div className="inner-header-section">{section}</div>
    </div>
  );
}

function InnerFooter({ page }) {
  return (
    <div className="inner-footer">
      <div className="inner-footer-text">KREPP Proposal · Confidential</div>
      <div className="inner-footer-pg">Page {page}</div>
    </div>
  );
}

// ── ROOT ───────────────────────────────────────────────────
export default function KreppProposal() {
  const [activePage, setActivePage] = useState(0);
  const pageComponents = [
    <CoverPage />, <TocPage />, <IntroPage />, <ModulesPage />,
    <LoansPage />, <AccountingPage />, <ValuePage />, <BenefitsPage />,
    <PricingPage />, <ContactPage />,
  ];

  return (
    <>
      <style>{styles}</style>
      <div className="nav-bar">
        <div className="nav-brand">KREPP</div>
        <div className="nav-pages">
          {pages.map((p, i) => (
            <button key={p} className={`nav-btn${activePage === i ? " active" : ""}`} onClick={() => setActivePage(i)}>{p}</button>
          ))}
        </div>
      </div>
      <div className="page-wrapper">
        {pageComponents[activePage]}
      </div>
    </>
  );
}